import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useNotes } from '@battleops/client/src/context/NoteContext';
import { useProcesses } from '@battleops/client/src/context/ProcessContext';
import { useLocalCollection } from '@battleops/client/src/data/useLocal';
import { PAGES, APP_LINKS, describeTarget } from '@battleops/shared';
import { usePanel } from '../hooks';

/** What reminder targets are looked up in (tasks, jobs, notes, events, routines) — for describeTarget and pickers. */
export function useTargetData() {
  const { tasks } = useTasks();
  const { projects } = useProjects();
  const { notes } = useNotes();
  const { processes } = useProcesses();
  const events = useLocalCollection('calendarEvents') || [];
  return useMemo(() => ({ tasks, projects, notes, events, processes: processes || [] }), [tasks, projects, notes, events, processes]);
}

export function useDescribeTarget() {
  const data = useTargetData();
  return useCallback((link) => describeTarget(link, data), [data]);
}

/** Go to what a reminder points at — the one router for the ring, the Reminders list and anywhere else. */
export function useOpenTarget() {
  const navigate = useNavigate();
  const panel = usePanel();
  return useCallback((link) => {
    if (!link) { navigate('/reminders'); return; }
    switch (link.type) {
      case 'task': panel.open('task', link.id); break;
      case 'project': navigate(`/projects/${link.id}`); break;
      case 'note': navigate(`/notes/all/${link.id}`); break;
      case 'event': panel.open('event', link.id); break;
      case 'block': navigate('/plan'); break;
      case 'approval': navigate('/approvals'); break;
      case 'process': case 'routine': navigate(link.id ? `/routines/${link.id}` : '/routines'); break;
      case 'page': navigate(PAGES.find((p) => p.id === link.id)?.web || '/'); break;
      case 'url': window.open(link.id, '_blank', 'noopener'); break;
      case 'app': { const a = APP_LINKS.find((x) => x.id === link.id); if (a?.web) window.open(a.web, '_blank', 'noopener'); break; }
      default: navigate('/reminders');
    }
  }, [navigate, panel]);
}
